import * as React from "react"
import { Container, Row, Col, Table, Spinner, Button } from "react-bootstrap"
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map } from "rxjs/operators"
import { toast } from "react-toastify"
import { AppPageWrapper } from "../../NavMenu/AppPageWrapper"
import TablePagination from "../../TablePagination"
import GridData from "../../../api/Randoms/GovPoolModel/GridData"
import GridRow from "../../../api/Randoms/GovPoolModel/GridRow"
import { ColumnFilter } from "../../../api/Randoms/GovPoolModel/ColumnFilter"
import ColumnHeaderFilter from "../../ColumnHeaderFilter"
import AddGovPoolModal from "./AddGovPoolModal"
import GovPoolDetailsModal from "./PoolDetailsModal"
import GovPoolDateSelectedModal from "./GovPoolDateSelectedModal"
import { PoolGridGetObservable } from "../../../api/Randoms/GovPoolGridGet"
import FormatDate from "../../../formatters/FormatDate"
import { connect } from "react-redux"
import { AppState } from "../../../AppState"
import { Filter, Sort } from "../../../api/GridRequest"
import { CustomizableFilterColumn } from "../../ColumnsCustomizeModal/CustomizableColumn"
import { Day, getDayName } from "../../../api/Randoms/GovPoolModel/DayOfWeek"
import GenderInfo from "../../../api/Randoms/GovPoolModel/GenderInfo"
import DaySelected from "../../../api/Randoms/GovPoolModel/DaySelected"
import { BuildUserAccess } from "../../../authentication/BuildUserAccess"

type Column = CustomizableFilterColumn<keyof GridRow, keyof ColumnFilter>
type SelectionInterval = 'Monthly' | 'Weekly'

interface PoolsProps {
  canManagePools?: boolean
  canManagePersonnel?: boolean
}

interface PoolsState {
  loading: boolean
  data?: GridData
  details?: GridRow
  detailsModal: boolean
  dateSelectedModal: boolean
  modal: boolean
  columns: Column[]
  dateSelectedID?: number
  dateSelected?: Date
}

interface PoolGridSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
  search: { field: string, text: string }
}

const PageSize = 10

const defaultColumns: Column[] = [
  { name: "Name", label: "GROUP NAME", visible: true, filter: "Name" },
  { name: "SelectionInterval", label: "SELECTION INTERVAL", visible: true, filter: "SelectionInterval" },
  { name: "SelectionDate", label: "SELECTION DATE", visible: true, filter: "SelectionDate" },
  { name: "DaysToSelect", label: "DAYS TO SELECT", visible: true, filter: "DaysToSelect" },
  { name: "GroupSize", label: "GROUP SIZE", visible: true, filter: "GroupSize" },
  { name: "GendersInfo", label: "GENDER", visible: true },
  { name: "DaysSelected", label: "DAYS SELECTED", visible: true }
]

class RandomGovPools extends React.PureComponent<PoolsProps, PoolsState> {
  state: PoolsState = {
    loading: false,
    detailsModal: false,
    dateSelectedModal: false,
    modal: false,
    columns: defaultColumns,
    dateSelectedID: undefined,
    dateSelected: undefined
  }

  page$ = new BehaviorSubject<PoolGridSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: [],
    search: { field: "", text: "" }
  })

  pageSubscription?: Subscription

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap((params: PoolGridSearchParams) => concat(
        of({ loading: true }),
        PoolGridGetObservable(params.pageNumber, PageSize, params.sort, params.filters, params.search).pipe(
          map((result: GridData) => ({ data: result, loading: false })))
      )
      )
    ).subscribe(
      (newState: { loading: boolean, data?: GridData }) => {
        if (newState.data) {
          if (newState.data.SelectionDate?.length) {
            newState.data.SelectionDate = newState.data.SelectionDate.map(dateString => FormatDate(dateString))
          }
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          this.setState({ loading: newState.loading })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  handlePageChange = (pageNumber: number) => this.page$.next({ ...this.page$.value, pageNumber: pageNumber })

  sortGrid(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.page$.next({ ...this.page$.value, sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  headerColumns = () => this.visibleColumns().map((col: Column, i) => {
    const sort = this.page$.value.sort
    return (
      <th key={col.name}>
        {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter
          title={col.label || col.name}
          filteredItems={this.page$.value.filters.find(item => item.column === col.filter)?.values || []}
          sort={sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
          items={this.state.data[col.filter as keyof ColumnFilter]?.filter(item => !!item) || []}
          onSort={(order?: string) => this.sortGrid(col.name, order)}
          onFilter={(items: string[]) => col.filter && this.filterGrid(col.filter, items)}
        />}
        {(!this.state.data || !col.filter) && (col.label || col.name)}
      </th>
    )
  })

  formatGender = (values: GenderInfo[]) => values?.map(o => `${o.Type}:${o.Count}`).join(', ')

  intersperse = (arr: React.ReactElement[], sep: string) => {
    if (arr.length === 0) {
      return []
    }
    return arr.slice(1).reduce(function (xs, x, i) {
      return xs.concat([<span key={i}>{sep}</span>, x]);
    }, [arr[0]])
  }

  formatDaysSelected = (row: GridRow) => {
    const { DaysSelected: daysSelected, SelectionInterval: selectionInterval } = row
    let days: React.ReactElement[] = []
    if (selectionInterval === "Weekly") {
      days = daysSelected?.sort((a, b) => a.DayOfWeek - b.DayOfWeek).map(elem => {
        if (new Date(elem.DateSelected) > new Date()) {
          return (<Button key={elem.DaysSelectedID} variant="link" className="p-0 align-baseline" onClick={() => this.showChangeDateModal(elem.DaysSelectedID, elem.DateSelected, row)}>{getDayName(elem.DayOfWeek)}</Button>)
        }
        else {
          return (<span key={elem.DaysSelectedID} className="p-0">{getDayName(elem.DayOfWeek)}</span>)
        }
      })
    }
    if (selectionInterval === "Monthly") {
      days = daysSelected?.sort((a, b) => new Date(a.DateSelected).getTime() - new Date(b.DateSelected).getTime()).map(elem => {
        if (new Date(elem.DateSelected) > new Date()) {
          return (<Button key={elem.DaysSelectedID} variant="link" className="p-0 align-baseline" onClick={() => this.showChangeDateModal(elem.DaysSelectedID, elem.DateSelected, row)}>{FormatDate(elem.DateSelected)}</Button>)
        }
        else {
          return (<span key={elem.DaysSelectedID} className="p-0">{FormatDate(elem.DateSelected)}</span>)
        }
      })
    }
    return this.intersperse(days, ", ");
  }

  renderRows = () => {
    if (this.state.data?.Records && this.state.data?.Records.length) {
      return this.state.data.Records.map((row: GridRow, i: number) =>
        <tr key={i}>
          <td><Button variant="link" onClick={() => this.showDetailsModal(row)} className="p-0">{row.Name}</Button></td>
          <td>{row.SelectionInterval}</td>
          <td>{FormatDate(row.SelectionDate)}</td>
          <td>{row.DaysToSelect > 0 && row.DaysToSelect}</td>
          <td>{row.GroupSize > 0 && row.GroupSize}</td>
          <td>{this.formatGender(row.GendersInfo)}</td>
          <td>{this.formatDaysSelected(row)}</td>
        </tr>)
    } else {
      return <tr><td colSpan={9}>No Records Found</td></tr>
    }
  }

  showDetailsModal = (details: GridRow) => this.setState({ detailsModal: true, details: details })

  showChangeDateModal = (dateSelectedID: number, dateSelected: Date, details: GridRow) => {
    this.setState({ dateSelectedModal: true, dateSelectedID: dateSelectedID, dateSelected: dateSelected, details: details })
  }

  hideDetailsModal = (hasUpdates: boolean) => {
    if (hasUpdates) {
      this.handlePageChange(1)
    }
    this.setState({ detailsModal: false, details: undefined })
  }

  hideDateSelectedModal = () => {
    this.setState({ dateSelectedModal: false })
  }

  showModal = () => this.setState({ modal: true })
  hideModal = () => this.setState({ modal: false })
  reload = () => this.page$.next({ ...this.page$.value, pageNumber: 1 })

  handleCreateSuccessful = () => {
    this.hideModal()
    this.reload()
  }

  handleUpdateSuccessful = () => {
    this.hideDateSelectedModal()
    this.reload()
  }

  render() {
    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        <Container fluid className="grid-container">
          <Row className="pb-0"><Col className="note-message font-italic px-3 text-center">IVR Phone Number: (833) 589-2187</Col></Row>
          <Row className="pb-2">
            <Col>
              {this.state.detailsModal && this.state.details && <GovPoolDetailsModal details={this.state.details} onHide={this.hideDetailsModal} />}
              {this.state.dateSelectedModal && this.state.details &&
                <GovPoolDateSelectedModal
                  interval={this.state.details.SelectionInterval as SelectionInterval}
                  onSuccess={this.handleUpdateSuccessful}
                  daysExcluded={this.state.details.DaysExcluded}
                  dateSelectedID={this.state.dateSelectedID || 0}
                  dateSelected={this.state.dateSelected || new Date()}
                  onHide={this.hideDateSelectedModal} />}
              {this.state.modal && <AddGovPoolModal onHide={this.hideModal} onSuccess={this.handleCreateSuccessful} />}
            </Col>
            <Col md={8} className="text-md-right mt-2 mt-md-0">
              <Button variant="primary" size="sm" onClick={this.showModal} className="mr-2">CREATE NEW GROUP</Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    {this.headerColumns()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr>
                        <td className="text-center" colSpan={8}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr> : this.renderRows()
                  }
                </tbody>
              </Table>
            </Col>
          </Row>
          {this.state.data ?
            <Row>
              <Col>
                <span className="total-records">Total records: {this.state.loading ? " ... " : this.state.data.Total}</span>
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.handlePageChange} />
              </Col>
            </Row> : undefined}
        </Container>
      </AppPageWrapper>
    )
  }
}
function mapStateToProps(state: AppState): PoolsProps {
  const userAccess = BuildUserAccess(state);
  return {
    canManagePools: userAccess.canManagePools,
    canManagePersonnel: userAccess.canManagePersonnel,
  }
}
const ConnectedRandomGovPools = connect(mapStateToProps, null)(RandomGovPools)
export default ConnectedRandomGovPools
